// Hours' model (timesheet rules §5): the team's pay period, from time.team, payroll.lockCheck and
// time.history, as the page will draw it (HoursView). Pure. To fix comes from each person's time.day
// once it's read (the area opened); until then, each person's counts from time.team. The lock lists
// every reason it's off in the server's words; start dates are a list to confirm. A manager sees the
// reasons and who locks it. A person's booked visits come from the board's own read (dispatch.board),
// so To fix and the quick edit offer the booked end. With no pay period yet (QA-757), Hours runs on
// payroll's weeks (weekOf), says in one quiet line that periods and locking come with payroll, and has
// no lock. Minutes and names only, never pay.
import { addDays, localDateOf } from '@flowology/kernel';
import type { HoursLockView, HoursView, ToFixRow } from '@flowology/ui-web/timesheet';
import { visitState } from '../board-bp-model.ts';
import { minuteOf } from '../board-drag-model.ts';
import { windowText, type Board } from '../board-model.ts';
import type { BookedVisit } from './quick-edit-model.ts';
import type {
  LockCheck,
  PayPeriod,
  TeamPerson,
  TimeDay,
  TimeHistoryItem,
  TimeViewer,
} from './timesheet-types.ts';
import { dayWords, minutesText, periodWords } from './time-words.ts';
import { nextPeriod } from '../books/payroll-model.ts';
import { changeViews, noPayrollLine, notOpenLine, payLineWords, toFixRows } from './timesheet-view-model.ts';

export interface HoursInput {
  period: PayPeriod;
  team: TeamPerson[];
  lock: LockCheck | null;
  history: TimeHistoryItem[];
  viewer: TimeViewer;
  /** Who locks hours (the bookkeeper), and who sends to payroll (the owner), by name. */
  locker: string | null;
  sender: string | null;
  /** Each person's days, once To fix is opened. */
  days?: Record<string, TimeDay[]>;
  booked?: Record<string, Record<string, BookedVisit[]>>;
  /** Start dates confirmed so far (staff ids), for withoutStatAndSickFor. */
  confirmed?: string[];
  pens?: Record<string, 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8>;
  today: string;
  timeZone: string;
  /** No pay period yet (QA-757): `period` is a week (weekOf); whether this viewer sets payroll up. */
  noPayroll?: { canSetUp: boolean };
  /** The next period isn't open yet (QA-774): `period` is it (notOpenYet); whether this viewer opens it. */
  notOpen?: { canOpen: boolean };
}

const firstName = (name: string) => name.split(' ')[0] ?? name;
/** Missed clock-outs first, then two clock-outs, then parts to check (rules §5). */
const rank = (r: ToFixRow) => (/never ended$/.test(r.problem) ? 0 : 2);

export function hoursLockView(input: HoursInput): HoursLockView | null {
  const { lock, viewer, period } = input;
  // only a pay period locks: a week before payroll is set up has nothing to prepare (QA-757)
  if (!lock || input.noPayroll || input.notOpen || isStandIn(period)) return null;
  const pw = periodWords(period.startDate, period.endDate);
  if (lock.locked) {
    const on = lock.lockedAt ? ` on ${dayWords(localDateOf(lock.lockedAt, input.timeZone))}` : '';
    return {
      reasons: [],
      confirm: [],
      button: null,
      locked: `Locked${lock.lockedBy ? ` by ${lock.lockedBy}` : ''}${on}. There’s no unlock.`,
      who: `${input.sender ?? 'The owner'} sends it to payroll.`,
    };
  }
  const reasons = lock.blockers.filter((b) => b.kind !== 'start_date').map((b) => b.words);
  const starts = lock.blockers.filter((b) => b.kind === 'start_date');
  const confirm = starts.flatMap((b) =>
    b.people.map((p) => ({ staffId: p.staffId, name: p.name, words: b.words })),
  );
  if (viewer === 'manager')
    return {
      reasons: [...reasons, ...starts.map((b) => b.words)],
      confirm: [],
      button: null,
      locked: null,
      who: `${input.locker ?? 'The owner'} prepares and locks it once nothing is left.`,
    };
  const ok = new Set(input.confirmed ?? []);
  const held = lock.blockers.some((b) => !b.confirmable) || confirm.some((c) => !ok.has(c.staffId));
  return {
    reasons,
    confirm,
    button: {
      label: `Prepare and lock ${pw}`,
      isDisabled: held,
      ask: `This prepares payroll for ${pw}, then locks its hours. They can’t change after this, and there’s no unlock. ${input.sender ?? 'The owner'} sends it to payroll for ${dayWords(period.payDate)}.`,
      notYet: 'Not yet',
    },
    locked: null,
    who: null,
  };
}

export function hoursView(input: HoursInput): HoursView {
  const { period, team } = input;
  const pw = periodWords(period.startDate, period.endDate);
  const toFix = input.days
    ? team
        .flatMap((p) =>
          (input.days![p.staffId] ?? []).flatMap((d) =>
            toFixRows(
              {
                staffId: p.staffId,
                name: p.name,
                first: firstName(p.name),
                pen: input.pens?.[p.staffId] ?? null,
              },
              d,
              {
                today: input.today,
                timeZone: input.timeZone,
                period,
                booked: input.booked?.[p.staffId]?.[d.date],
              },
            ),
          ),
        )
        .map((r, i) => ({ r, i }))
        .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)
        .map(({ r }) => r)
    : [];
  const sum = (f: (p: TeamPerson) => number) => team.reduce((n, p) => n + f(p), 0);
  // QA-815: once the days are read, To fix counts what it lists (a day never ended is a row there that
  // time.team's counts don't have); until then, time.team's counts
  const listed = new Map<string, number>();
  for (const r of toFix) listed.set(r.staffId, (listed.get(r.staffId) ?? 0) + 1);
  const fixesOf = (p: TeamPerson) => (input.days ? (listed.get(p.staffId) ?? 0) : p.leftOpen + p.flagged);
  const fixes = sum(fixesOf);
  const showAt2 = team.some((p) => p.split.double > 0);
  return {
    title: `Hours · ${pw}`,
    payLine: input.noPayroll || input.notOpen ? '' : payLineWords(period, input.timeZone),
    noPayroll: input.noPayroll
      ? noPayrollLine(input.noPayroll.canSetUp)
      : input.notOpen
        ? notOpenLine(period, input.notOpen.canOpen)
        : null,
    facts: [
      { label: 'To fix', value: String(fixes), ...(fixes ? { tone: 'bad' as const } : {}) },
      { label: 'Paid by the team', value: minutesText(sum((p) => p.split.paid)) },
      {
        label: 'Past 8 h a day or 40 h a week',
        value: minutesText(sum((p) => p.split.overtime + p.split.double)),
      },
    ],
    toFix,
    toFixCounts: input.days
      ? []
      : team
          .filter((p) => p.leftOpen + p.flagged > 0)
          .map((p) => ({ staffId: p.staffId, name: p.name, words: countWords(p) })),
    people: team.map((p) => {
      const n = fixesOf(p);
      return {
        staffId: p.staffId,
        name: p.name,
        paid: minutesText(p.split.paid),
        at15: minutesText(p.split.overtime),
        at2: showAt2 ? minutesText(p.split.double) : null,
        toFix: { value: String(n), ...(n ? { tone: 'bad' as const } : {}) },
        // the old Hours' "Working now": a part of theirs running today
        now: p.runningNow > 0 ? 'Working now' : null,
      };
    }),
    showAt2,
    lock: hoursLockView(input),
    // every change to the period's hours, newest first (QA-815): the feed shows the latest and Show all
    changes: changeViews(input.history, input.timeZone),
  };
}

/**
 * A person's visits on a day as the board has them (dispatch.board, the read the board draws from), at
 * their booked windows: the quick edit's Booked row and the fixes' booked end. Cancelled ones aren't.
 */
export function bookedFromBoard(
  board: Pick<Board, 'technicians'>,
  staffId: string,
  date: string,
  timeZone: string,
): BookedVisit[] {
  const tech = board.technicians.find((t) => t.id === staffId);
  return (tech?.visits ?? [])
    .filter((v) => v.status !== 'cancelled' && localDateOf(v.start, timeZone) === date)
    .map((v) => {
      const start = minuteOf(v.start, date, timeZone);
      const st = visitState(v, staffId);
      // the visit's own times, which its booked end is the end of (QA-762, QA-774); the arrival window,
      // where it differs, is said as the window
      const own = windowText({ start: v.start, end: v.end, arrivalWindow: null }, timeZone);
      const arrival = v.arrivalWindow ? windowText(v, timeZone) : null;
      return {
        id: v.appointmentId,
        jobId: v.jobId,
        customer: v.customerName,
        startMinute: start,
        endMinute: Math.max(minuteOf(v.end, date, timeZone), start + 5),
        drawn: st === 'done' ? 'done' : st === 'working' ? 'working' : 'booked',
        time: own,
        job: `#${v.jobNumber}`,
        ...(arrival && arrival !== own ? { arrival } : {}),
      };
    });
}

/** "1 clock-out missing, 2 to check" */
function countWords(p: TeamPerson): string {
  const bits = [];
  if (p.leftOpen) bits.push(`${p.leftOpen} clock-out${p.leftOpen === 1 ? '' : 's'} missing`);
  if (p.flagged) bits.push(`${p.flagged} to check`);
  return bits.join(', ');
}

/** Who's looking, for the lock and the lock line: the owner, the bookkeeper, or (anyone else who fixes time) a manager. */
export function timeViewer(roles: readonly string[] | undefined, permissions: readonly string[]): TimeViewer {
  const r = roles ?? [];
  if (r.includes('owner')) return 'owner';
  if (r.includes('bookkeeper')) return 'bookkeeper';
  if (r.includes('manager')) return 'manager';
  return permissions.includes('payroll.prepare') ? 'bookkeeper' : 'manager';
}

/** The period a day is in; else the latest one that has started; else the earliest. */
export function periodOn(periods: readonly PayPeriod[], date: string): PayPeriod | null {
  const by = [...periods].sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
  return (
    by.find((p) => p.startDate <= date && date <= p.endDate) ??
    [...by].reverse().find((p) => p.startDate <= date) ??
    by[0] ??
    null
  );
}

/** The period choices, oldest first, at most the latest `max` that have started. */
export function periodSegments(periods: readonly PayPeriod[], today: string, max = 4) {
  return [...periods]
    .filter((p) => p.startDate <= today)
    .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
    .slice(-max)
    .map((p) => ({ id: p.id, label: periodWords(p.startDate, p.endDate) }));
}

/**
 * With no pay period yet (QA-757): the week `date` is in, on payroll.weekStartsOn as it's set (0 =
 * Sunday), standing in for a period so the team's hours, To fix, the quick edit, the changes and the
 * overtime split all work. It's open and nothing pays on it; only a pay period prepares and locks.
 */
export function weekOf(date: string, weekStartsOn: number): PayPeriod {
  const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
  const startDate = addDays(date, -((dow - weekStartsOn + 7) % 7));
  const endDate = addDays(startDate, 6);
  return {
    id: `week:${startDate}`,
    startDate,
    endDate,
    payDate: endDate,
    status: 'open',
    lockedAt: null,
    lockedBy: null,
    sentAt: null,
    sentBy: null,
  };
}

/** A week standing in for a pay period (weekOf), never one of the company's. */
export const isWeek = (p: Pick<PayPeriod, 'id'>) => p.id.startsWith('week:');

/**
 * Today past the newest pay period's end (QA-774): the period that would come next, as Payroll opens
 * it (nextPeriod: the newest one's length, on from its end), standing in until someone opens it, so
 * this week's hours and To fix show. It isn't one of the company's: it doesn't lock, and Hours says
 * it isn't open yet and who opens it. Null while a period covers today.
 */
export function notOpenYet(periods: readonly PayPeriod[], today: string): PayPeriod | null {
  if (!periods.length || periods.some((p) => p.startDate <= today && today <= p.endDate)) return null;
  let newest = [...periods].sort((a, b) => (a.endDate < b.endDate ? 1 : -1))[0]!;
  if (newest.endDate >= today) return null;
  for (let i = 0; i < 60; i++) {
    const next = nextPeriod(newest, today);
    const p: PayPeriod = {
      ...next,
      id: `next:${next.startDate}`,
      status: 'open',
      lockedAt: null,
      lockedBy: null,
      sentAt: null,
      sentBy: null,
    };
    if (p.endDate >= today) return p;
    newest = p;
  }
  return null;
}

/** A period that's only a stand-in (a week before payroll, or the next one not open yet). */
export const isStandIn = (p: Pick<PayPeriod, 'id'>) => isWeek(p) || p.id.startsWith('next:');
